import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getNavigation, getPages } from '@/api/wiki'
import type { WikiCategory, WikiPage } from '@/types/wiki'

type WikiNavContextValue = {
  categories: WikiCategory[]
  pages: WikiPage[]
  loading: boolean
  error: boolean
  refresh: () => void

  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void
  toggleMobileNav: () => void
}

const WikiNavContext = createContext<WikiNavContextValue | null>(null)

export function WikiNavProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<WikiCategory[]>([])
  const [pages, setPages] = useState<WikiPage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const location = useLocation()

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const [navData, pagesData] = await Promise.all([getNavigation(), getPages()])
      setCategories(navData.categories || [])
      setPages(pagesData || [])
    } catch (e) {
      console.error(e)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Close mobile nav on route changes.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  // Prevent background scrolling when the drawer is open.
  useEffect(() => {
    const prev = document.body.style.overflow
    if (mobileNavOpen) document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileNavOpen])

  const value = useMemo<WikiNavContextValue>(() => ({
    categories,
    pages,
    loading,
    error,
    refresh: load,
    mobileNavOpen,
    setMobileNavOpen,
    toggleMobileNav: () => setMobileNavOpen(v => !v),
  }), [categories, pages, loading, error, load, mobileNavOpen])

  return (
    <WikiNavContext.Provider value={value}>
      {children}
    </WikiNavContext.Provider>
  )
}

export function useWikiNav() {
  const ctx = useContext(WikiNavContext)
  if (!ctx) throw new Error('useWikiNav must be used within WikiNavProvider')
  return ctx
}

